import { useEffect, useRef, useState } from 'react';

import { faMicrophone, faMicrophoneSlash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from 'reactstrap';
import { Subscription } from 'rxjs';

import { notificationService } from 'subjects/common/NotificationService';

import { hangUpOutgoingCall } from '../helpers';

const ParticipantInfo = ({
    participant,
    sendMessage,
    saveAuditTrail,
    participantName,
    isPhoneCall,
    telehealthModuleType,
    setParticipants,
}) => {
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [kickParticipant, setKickParticipant] = useState(false);
    const [muteParticipant, setMuteParticipant] = useState(false);

    const hangUpOutboundCallSubscription = useRef<Subscription | null>(null);

    const handleTrackDisabled = (track) => {
        if (track.kind === 'audio') {
            track.on('disabled', () => {
                setAudioEnabled(track.isEnabled);
                setMuteParticipant(false);
            });

            track.on('enabled', () => {
                setAudioEnabled(track.isEnabled);
                setMuteParticipant(false);
            });
        }
    };

    useEffect(() => {
        participant.tracks.forEach((publication) => {
            if (publication.isSubscribed) {
                handleTrackDisabled(publication.track);
            }
            publication.on('subscribed', handleTrackDisabled);

            if (publication.track && publication.track.kind === 'audio') {
                publication.track.on('disabled', handleTrackDisabled);
                publication.track.on('enabled', handleTrackDisabled);
            }
        });
    }, [participant]);

    useEffect(() => {
        return () => {
            if (hangUpOutboundCallSubscription.current) {
                hangUpOutboundCallSubscription.current.unsubscribe();
                hangUpOutboundCallSubscription.current = null;
            }
        };
    }, []);

    const handleKickParticipant = () => {
        setKickParticipant(true);
        if(isPhoneCall){
            handleHangupParticipant(participant.callSid);
            saveAuditTrail('Provider hangup the phone attendee');
            return;
        }
        sendMessage(participant, 'logout');
        saveAuditTrail('Provider Kicked Out Attendee');
    };

    const handleMuteParticipant = () => {
        setMuteParticipant(true);
        sendMessage(participant, 'mute');
    };

    const handleHangupParticipant = (callSid) => {
        if (!callSid){
            return;
        }

        if (hangUpOutboundCallSubscription.current) {
            hangUpOutboundCallSubscription.current.unsubscribe();
        }

        hangUpOutboundCallSubscription.current = hangUpOutgoingCall(telehealthModuleType, callSid).subscribe({
            next: (response) => {
                if (response.status === 200) {
                    setParticipants((prevParticipants) => {
                        return prevParticipants.filter((p) => !p.identity.includes(callSid));
                    });
                    setKickParticipant(false);
                    notificationService.success({
                        title: 'The outgoing call has been hung up successfully',
                    });
                } else {
                    notificationService.error({
                        title: 'Failed to hang up the outgoing call, please try again later.',
                    });
                }
            },
            error: () => {
                notificationService.error({
                    title: 'Failed to hang up the outgoing call, please try again later.',
                });
            },
        });
    };

    return (
        <div
            key={participant.sid}
            className='participant-item'
        >
            <span>{participantName}</span>
                <div className='participant-buttons'>
                    {!isPhoneCall && (
                        <div
                            className='participant-btn handle-audio'
                            onClick={handleMuteParticipant}
                        >
                            {muteParticipant ? (
                                <Spinner
                                    style={{
                                        position: 'relative',
                                        color: '#e3165b',
                                        height: '15px',
                                        width: '15px',
                                    }}
                                />
                            ) : audioEnabled ? (
                                <FontAwesomeIcon
                                    className='microphone-icon'
                                    icon={faMicrophone}
                                />
                            ) : (
                                <FontAwesomeIcon
                                    className='microphone-slash-icon'
                                    icon={faMicrophoneSlash}
                                />
                            )}
                        </div>
                    )}
                    <div
                        className='participant-btn handle-dismiss-participant'
                        onClick={handleKickParticipant}
                    >
                        {kickParticipant ? (
                            <Spinner
                                style={{
                                    position: 'relative',
                                    color: '#e3165b',
                                    height: '15px',
                                    width: '15px',
                                }}
                            />
                        ) : (
                            <FontAwesomeIcon
                                className='cross-icon'
                                icon={faTimes}
                            />
                        )}
                    </div>
                </div>
        </div>
    );
};

export default ParticipantInfo;
